import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Printer, Download, Upload, Search, Trash2, Phone, TrendingUp, Coins, Wallet, AlertTriangle, MessageCircle, BarChart3, Calendar, Eye, EyeOff, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { HiddenMoney, useGlobalVisibility } from "../context/PriceVisibilityContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";

function fmtMoney(n) {
  const num = Number(n || 0);
  return num.toLocaleString("en-US", { maximumFractionDigits: 0 });
}

function highlight(text, query) {
  if (!query || !text) return text;
  const str = String(text);
  const q = query.trim();
  if (!q) return str;
  const idx = str.toLowerCase().indexOf(q.toLowerCase());
  if (idx === -1) return str;
  return (
    <>
      {str.slice(0, idx)}
      <span className="search-highlight">{str.slice(idx, idx + q.length)}</span>
      {str.slice(idx + q.length)}
    </>
  );
}

function SortIcon({ active, dir }) {
  if (!active) return <ArrowUpDown className="h-3.5 w-3.5 opacity-40" />;
  return dir === "asc" ? <ArrowUp className="h-3.5 w-3.5 text-teal-700" /> : <ArrowDown className="h-3.5 w-3.5 text-teal-700" />;
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showAll, hideAll } = useGlobalVisibility();
  const [allVisible, setAllVisible] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [openAdd, setOpenAdd] = useState(false);
  const [deleting, setDeleting] = useState(null); // { id, name, serial }
  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    address: "",
    notes: "",
    total_amount: "",
  });
  const [saving, setSaving] = useState(false);
  const [periodType, setPeriodType] = useState("daily");
  const [periodValue, setPeriodValue] = useState(new Date().toISOString().slice(0, 10));
  const [sortBy, setSortBy] = useState(null); // 'name' | 'last_payment_date' | 'total_amount' | 'remaining'
  const [sortDir, setSortDir] = useState("asc"); // 'asc' | 'desc'

  const toggleSort = (key) => {
    if (sortBy === key) {
      // Cycle: asc -> desc -> none
      if (sortDir === "asc") setSortDir("desc");
      else { setSortBy(null); setSortDir("asc"); }
    } else {
      setSortBy(key);
      setSortDir("asc");
    }
  };

  const toggleAllVisibility = () => {
    if (allVisible) { hideAll(); setAllVisible(false); }
    else { showAll(); setAllVisible(true); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/customers");
      setCustomers(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = customers;
    if (q) {
      list = customers.filter(
        (c) =>
          c.full_name?.toLowerCase().includes(q) ||
          c.phone?.toLowerCase().includes(q) ||
          String(c.serial_number).includes(q)
      );
    }
    if (sortBy) {
      const dir = sortDir === "asc" ? 1 : -1;
      list = [...list].sort((a, b) => {
        let va, vb;
        if (sortBy === "name") {
          va = (a.full_name || "").trim();
          vb = (b.full_name || "").trim();
          return va.localeCompare(vb, "ar") * dir;
        }
        if (sortBy === "last_payment_date") {
          va = a.last_payment_date || "";
          vb = b.last_payment_date || "";
          // Empty dates sort to the end always
          if (!va && !vb) return 0;
          if (!va) return 1;
          if (!vb) return -1;
          return va.localeCompare(vb) * dir;
        }
        if (sortBy === "total_amount") {
          return ((Number(a.total_amount) || 0) - (Number(b.total_amount) || 0)) * dir;
        }
        if (sortBy === "remaining") {
          return ((Number(a.remaining) || 0) - (Number(b.remaining) || 0)) * dir;
        }
        return 0;
      });
    }
    return list;
  }, [customers, query, sortBy, sortDir]);

  const totalsAll = useMemo(() => {
    return customers.reduce(
      (acc, c) => {
        acc.total += Number(c.total_amount || 0);
        acc.paid += Number(c.total_paid || 0);
        acc.remaining += Number(c.remaining || 0);
        return acc;
      },
      { total: 0, paid: 0, remaining: 0 }
    );
  }, [customers]);

  // Overdue: remaining > 0 AND days since last payment (or creation) > 30
  const overdueCustomers = useMemo(() => {
    const now = Date.now();
    const items = customers
      .map((c) => {
        if (!c.remaining || c.remaining <= 0) return null;
        const refDateStr = c.last_payment_date || (c.created_at ? c.created_at.slice(0, 10) : null);
        if (!refDateStr) return null;
        const refTime = new Date(refDateStr).getTime();
        if (isNaN(refTime)) return null;
        const days = Math.floor((now - refTime) / (24 * 60 * 60 * 1000));
        if (days <= 30) return null;
        return { ...c, days_overdue: days, ref_date: refDateStr, has_paid_before: !!c.last_payment_date };
      })
      .filter(Boolean)
      .sort((a, b) => b.days_overdue - a.days_overdue);
    return items;
  }, [customers]);

  const totalOverdueAmount = overdueCustomers.reduce((s, c) => s + Number(c.remaining || 0), 0);

  // Period collections (daily/monthly/yearly)
  const periodCollected = useMemo(() => {
    const matches = (dateStr) => {
      if (!dateStr) return false;
      if (periodType === "daily") return dateStr === periodValue;
      if (periodType === "monthly") return dateStr.startsWith(periodValue); // YYYY-MM
      if (periodType === "yearly") return dateStr.startsWith(periodValue); // YYYY
      return false;
    };
    let total = 0;
    let mainCount = 0;
    let purchaseCount = 0;
    let mainTotal = 0;
    let purchaseTotal = 0;
    for (const c of customers) {
      for (const p of c.payments || []) {
        if (matches(p.payment_date)) { total += Number(p.amount || 0); mainCount++; mainTotal += Number(p.amount || 0); }
      }
      for (const pur of c.purchases || []) {
        for (const pp of pur.payments || []) {
          if (matches(pp.payment_date)) { total += Number(pp.amount || 0); purchaseCount++; purchaseTotal += Number(pp.amount || 0); }
        }
      }
    }
    return { total, mainCount, purchaseCount, mainTotal, purchaseTotal, count: mainCount + purchaseCount };
  }, [customers, periodType, periodValue]);

  const switchPeriod = (type) => {
    setPeriodType(type);
    const now = new Date();
    if (type === "daily") setPeriodValue(now.toISOString().slice(0, 10));
    else if (type === "monthly") setPeriodValue(now.toISOString().slice(0, 7));
    else setPeriodValue(String(now.getFullYear()));
  };

  const buildWhatsAppLink = (customer) => {
    let phone = (customer.phone || "").replace(/\D/g, "");
    if (phone.startsWith("00964")) phone = phone.slice(2);
    else if (phone.startsWith("0")) phone = "964" + phone.slice(1);
    else if (!phone.startsWith("964")) phone = "964" + phone;

    const remainingFmt = Number(customer.remaining || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });
    const lastDate = customer.last_payment_date || "—";
    const msg = `السلام عليكم ${customer.full_name} 🌹\n\nنذكركم بشكل ودي بأن لديكم قسطاً متبقياً من متجر آغا فون بقيمة *${remainingFmt} د.ع*\n\n${customer.has_paid_before ? `آخر تسديد كان بتاريخ: ${lastDate}\nمضى على آخر تسديد: ${customer.days_overdue} يوم` : `لم يتم تسجيل أي تسديد منذ ${customer.days_overdue} يوم`}\n\nنرجو التواصل معنا لتنسيق موعد التسديد.\n\nشكراً لتعاونكم 🙏\n— متجر آغا فون`;
    return `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
  };


  const submitAdd = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.phone.trim()) {
      toast.error("الاسم ورقم الهاتف مطلوبان");
      return;
    }
    const amt = parseFloat(form.total_amount);
    if (isNaN(amt) || amt < 0) {
      toast.error("الرجاء إدخال مبلغ صحيح");
      return;
    }
    setSaving(true);
    try {
      await api.post("/customers", { ...form, total_amount: amt });
      toast.success("تم إضافة الزبون");
      setOpenAdd(false);
      setForm({ full_name: "", phone: "", address: "", notes: "", total_amount: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await api.delete(`/customers/${deleting.id}`);
      toast.success(`تم حذف الزبون "${deleting.name}"`);
      setDeleting(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleBackup = async () => {
    try {
      const { data } = await api.get("/backup");
      const customersCount = data.customers?.length || 0;
      const dt = new Date();
      const dtStr = dt.toLocaleDateString("en-GB");
      const timeStr = dt.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

      // Build enriched customer list with payment info
      const customersWithInfo = (data.customers || []).map((c) => {
        const cPayments = (data.payments || []).filter((p) => p.customer_id === c.id);
        const totalPaid = cPayments.reduce((s, p) => s + Number(p.amount || 0), 0);
        const lastPayment = cPayments.length
          ? cPayments.sort((a, b) => (a.payment_date < b.payment_date ? 1 : -1))[0].payment_date
          : "—";
        return {
          ...c,
          total_paid: totalPaid,
          remaining: Number(c.total_amount || 0) - totalPaid,
          last_payment_date: lastPayment,
          payments_count: cPayments.length,
        };
      });
      customersWithInfo.sort((a, b) => a.serial_number - b.serial_number);

      const totalAll = customersWithInfo.reduce((s, c) => s + Number(c.total_amount || 0), 0);
      const totalPaidAll = customersWithInfo.reduce((s, c) => s + Number(c.total_paid || 0), 0);
      const totalRemainingAll = customersWithInfo.reduce((s, c) => s + Number(c.remaining || 0), 0);

      const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });

      // Embed restore JSON as base64 inside HTML so this single file works for both viewing AND restoring
      const restoreJson = JSON.stringify(data);
      const restoreB64 = btoa(unescape(encodeURIComponent(restoreJson)));

      const rows = customersWithInfo.map((c) => `
        <tr>
          <td class="num center">${c.serial_number}</td>
          <td class="name">${(c.full_name || "").replace(/</g, "&lt;")}</td>
          <td class="num ltr">${(c.phone || "").replace(/</g, "&lt;")}</td>
          <td class="num center">${c.last_payment_date || "—"}</td>
          <td class="num total">${fmt(c.total_amount)}</td>
          <td class="num paid">${fmt(c.total_paid)}</td>
          <td class="num remaining ${c.remaining > 0 ? "owed" : "done"}">${fmt(c.remaining)}</td>
        </tr>
      `).join("");

      const html = `<!doctype html>
<html lang="ar" dir="rtl">
<head>
<meta charset="utf-8">
<title>نسخة احتياطية - آغا فون - ${dtStr}</title>
<link href="https://fonts.googleapis.com/css2?family=Almarai:wght@400;700;800&family=Reem+Kufi:wght@600;700&family=Rubik:wght@500;700&display=swap" rel="stylesheet">
<script type="application/json" id="agha-restore-data">${restoreB64}<\/script>
<style>
  * { box-sizing: border-box; }
  body { font-family: 'Almarai', Tahoma, sans-serif; background: #F4F1EA; margin: 0; padding: 30px; color: #1A1410; }
  .page { max-width: 1200px; margin: 0 auto; background: white; border-radius: 16px; box-shadow: 0 4px 30px rgba(0,0,0,0.08); overflow: hidden; }
  .header { background: linear-gradient(135deg, #0F766E 0%, #134E4A 100%); color: white; padding: 30px; text-align: center; position: relative; }
  .header h1 { font-family: 'Reem Kufi', sans-serif; margin: 0; font-size: 36px; font-weight: 700; }
  .header .sub { font-family: 'Rubik', sans-serif; margin-top: 6px; opacity: 0.85; font-size: 14px; letter-spacing: 2px; }
  .header .meta { margin-top: 14px; display: inline-flex; gap: 20px; padding: 8px 24px; background: rgba(255,255,255,0.1); border-radius: 999px; font-size: 13px; backdrop-filter: blur(10px); }
  .stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; padding: 24px; background: #FAFAF7; border-bottom: 1px solid #e5e5e5; }
  .stat { background: white; padding: 16px; border-radius: 10px; border: 1px solid #eee; }
  .stat .label { font-size: 11px; color: #888; letter-spacing: 1px; text-transform: uppercase; margin-bottom: 6px; font-family: 'Rubik', sans-serif; font-weight: 600; }
  .stat .value { font-size: 22px; font-weight: 800; font-family: 'Rubik', sans-serif; }
  .stat.total .value { color: #1A1410; }
  .stat.paid .value { color: #059669; }
  .stat.remaining { background: linear-gradient(135deg, #F97316, #EA580C); color: white; }
  .stat.remaining .label { color: rgba(255,255,255,0.85); }
  .stat.remaining .value { color: white; }
  .stat.count .value { color: #0F766E; }
  table { width: 100%; border-collapse: collapse; }
  thead { background: #F9FAFB; }
  th { padding: 14px 12px; text-align: right; font-size: 12px; font-weight: 700; color: #555; border-bottom: 2px solid #e5e5e5; text-transform: uppercase; letter-spacing: 0.5px; }
  th.center, td.center { text-align: center; }
  td { padding: 14px 12px; border-bottom: 1px solid #f0f0f0; font-size: 14px; }
  tr:hover { background: #FEFCE8; }
  .num { font-family: 'Rubik', sans-serif; font-variant-numeric: tabular-nums; }
  .ltr { direction: ltr; text-align: right; }
  .name { font-weight: 700; }
  .total { font-weight: 700; }
  .paid { color: #059669; font-weight: 700; }
  .remaining { font-weight: 800; }
  .remaining.owed { color: #EA580C; }
  .remaining.done { color: #059669; }
  .footer { padding: 20px 30px; background: #FAFAF7; border-top: 1px solid #e5e5e5; text-align: center; font-size: 12px; color: #888; letter-spacing: 1px; font-family: 'Rubik', sans-serif; }
  .footer strong { color: #0F766E; }
  .actions { padding: 18px 30px; background: #FFFBEB; border-top: 1px solid #FDE68A; display: flex; gap: 10px; flex-wrap: wrap; align-items: center; justify-content: center; }
  .actions button { font-family: 'Almarai', sans-serif; cursor: pointer; padding: 10px 22px; border-radius: 8px; border: none; font-weight: 700; font-size: 14px; transition: all 0.2s; }
  .btn-print { background: #0F766E; color: white; }
  .btn-print:hover { background: #134E4A; }
  .btn-restore { background: #F59E0B; color: white; }
  .btn-restore:hover { background: #D97706; }
  .info { font-size: 13px; color: #92400E; max-width: 600px; text-align: center; }
  .empty { padding: 60px 20px; text-align: center; color: #999; }
  @media print {
    body { background: white; padding: 0; }
    .page { box-shadow: none; border-radius: 0; }
    .actions { display: none; }
    tr:hover { background: transparent; }
  }
</style>
</head>
<body>
<div class="page">
  <div class="header">
    <h1>آغا فون · AGHA PHONE</h1>
    <div class="sub">MOBILE · ACCESSORIES</div>
    <div class="meta">
      <span>📅 ${dtStr} — ${timeStr}</span>
      <span>👥 عدد الزبائن: <strong>${customersCount}</strong></span>
    </div>
  </div>

  <div class="stats">
    <div class="stat count"><div class="label">Customers</div><div class="value">${customersCount}</div></div>
    <div class="stat total"><div class="label">Total</div><div class="value">${fmt(totalAll)} <small style="font-size:11px;color:#888">د.ع</small></div></div>
    <div class="stat paid"><div class="label">Paid</div><div class="value">${fmt(totalPaidAll)} <small style="font-size:11px;color:#888">د.ع</small></div></div>
    <div class="stat remaining"><div class="label">Remaining</div><div class="value">${fmt(totalRemainingAll)} <small style="font-size:11px;opacity:0.85">د.ع</small></div></div>
  </div>

  ${customersCount === 0 ? '<div class="empty">لا يوجد زبائن</div>' : `
  <table>
    <thead>
      <tr>
        <th class="center" style="width:60px">#</th>
        <th>الاسم الثلاثي</th>
        <th>رقم الهاتف</th>
        <th class="center">تاريخ آخر تسديد</th>
        <th>المبلغ الكلي</th>
        <th>المسدد</th>
        <th>المتبقي</th>
      </tr>
    </thead>
    <tbody>${rows}</tbody>
  </table>
  `}

  <div class="actions">
    <button class="btn-print" onclick="window.print()">🖨️ طباعة</button>
    <button class="btn-restore" onclick="downloadRestore()">💾 تنزيل ملف الاستعادة (JSON)</button>
    <div class="info">⚠️ هذا الملف يحتوي نسخة عرض مرئية + بيانات استعادة مدمجة. لاستعادة البيانات على حاسوب آخر، اضغط "تنزيل ملف الاستعادة" ثم ارفعه عبر زر "تحديث" داخل تطبيق آغا فون.</div>
  </div>

  <div class="footer">
    AGHA PHONE · BACKUP REPORT · Generated ${dtStr}
  </div>
</div>

<script>
function downloadRestore() {
  const b64 = document.getElementById('agha-restore-data').textContent;
  const json = decodeURIComponent(escape(atob(b64)));
  const blob = new Blob([json], { type: 'application/json' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = 'agha_phone_restore_' + new Date().toISOString().slice(0,10) + '.json';
  a.click();
  URL.revokeObjectURL(a.href);
}
<\/script>
</body>
</html>`;

      const blob = new Blob([html], { type: "text/html;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `agha_phone_${customersCount}_customers_${dt.toISOString().slice(0, 10)}.html`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`تم تنزيل النسخة الاحتياطية (${customersCount} زبون)`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const handleRestore = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (user?.role !== "admin") {
      toast.error("هذه الصلاحية للأدمن فقط");
      return;
    }
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const content = ev.target.result;
        let json;
        if (file.name.toLowerCase().endsWith(".html") || content.trim().startsWith("<")) {
          // Extract embedded base64 JSON from HTML backup
          const match = content.match(/<script[^>]*id="agha-restore-data"[^>]*>([\s\S]*?)<\/script>/);
          if (!match) throw new Error("no embedded data");
          const b64 = match[1].trim();
          const jsonStr = decodeURIComponent(escape(atob(b64)));
          json = JSON.parse(jsonStr);
        } else {
          json = JSON.parse(content);
        }
        const res = await api.post("/restore", json);
        toast.success(`تم تحديث البيانات (${res.data.customers} زبون)`);
        load();
      } catch (err) {
        toast.error("ملف غير صالح");
      }
    };
    reader.readAsText(file);
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-8">
      {/* Header / toolbar */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-7 no-print">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-teal-100 text-teal-800 rounded-full text-xs font-bold mb-3 font-latin tracking-wide">
            <span className="h-1.5 w-1.5 rounded-full bg-teal-600 animate-pulse" />
            DASHBOARD
          </div>
          <h1 className="text-4xl font-display font-extrabold text-zinc-900 tracking-tight">الصفحة الرئيسية</h1>
          <p className="text-sm text-zinc-600 mt-1.5">
            قائمة الزبائن والأقساط — <span className="font-bold text-teal-700 num">{customers.length}</span> زبون
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            data-testid="toggle-all-visibility-btn"
            onClick={toggleAllVisibility}
            className="border-zinc-300 bg-white hover:bg-zinc-50 hover:border-zinc-400 font-semibold"
          >
            {allVisible ? <EyeOff className="h-4 w-4 ms-2" /> : <Eye className="h-4 w-4 ms-2" />}
            {allVisible ? "إخفاء الكل" : "إظهار الكل"}
          </Button>
          <Button
            variant="outline"
            data-testid="print-btn"
            onClick={handlePrint}
            className="border-zinc-300 bg-white hover:bg-zinc-50 hover:border-zinc-400 font-semibold"
          >
            <Printer className="h-4 w-4 ms-2" />
            طباعة
          </Button>
          <Button
            data-testid="backup-btn"
            onClick={handleBackup}
            className="bg-amber-500 hover:bg-amber-600 text-white shadow-sm shadow-amber-500/30 font-semibold"
          >
            <Download className="h-4 w-4 ms-2" />
            نسخة احتياطية
          </Button>
          {user?.role === "admin" && (
            <label className="cursor-pointer">
              <input
                type="file"
                accept="application/json,.json,text/html,.html,.htm"
                className="hidden"
                onChange={handleRestore}
                data-testid="update-file-input"
              />
              <div className="inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-semibold border border-zinc-300 bg-white hover:bg-zinc-50 hover:border-zinc-400 transition-all">
                <Upload className="h-4 w-4 ms-2" />
                تحديث
              </div>
            </label>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-6 no-print">
        <Search className="h-5 w-5 text-zinc-400 absolute right-4 top-1/2 -translate-y-1/2" />
        <Input
          data-testid="search-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="ابحث بالاسم أو رقم الهاتف أو الرقم التسلسلي..."
          className="pe-12 py-7 text-base bg-white border-2 border-zinc-200 focus-visible:border-teal-600 focus-visible:ring-0 rounded-xl shadow-sm"
        />
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-7 no-print">
        <div className="bg-white rounded-2xl border border-zinc-200 elevated-card px-6 py-5 flex items-center gap-4">
          <div className="h-12 w-12 rounded-xl bg-teal-100 text-teal-700 flex items-center justify-center">
            <Coins className="h-6 w-6" />
          </div>
          <div className="flex-1">
            <div className="text-xs text-zinc-500 uppercase tracking-wider mb-1 font-latin font-semibold">إجمالي المبالغ</div>
            <div className="text-2xl font-extrabold text-zinc-900 num" data-testid="stat-total"><HiddenMoney value={fmtMoney(totalsAll.total)} /><span className="iqd-tag">د.ع</span></div>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-zinc-200 elevated-card px-6 py-5 flex items-center gap-4">
          <div className="h-12 w-12 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center">
            <TrendingUp className="h-6 w-6" />
          </div>
          <div className="flex-1">
            <div className="text-xs text-zinc-500 uppercase tracking-wider mb-1 font-latin font-semibold">المسدد</div>
            <div className="text-2xl font-extrabold text-emerald-600 num" data-testid="stat-paid"><HiddenMoney value={fmtMoney(totalsAll.paid)} /><span className="iqd-tag">د.ع</span></div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-orange-500 to-amber-600 rounded-2xl elevated-card px-6 py-5 flex items-center gap-4 text-white">
          <div className="h-12 w-12 rounded-xl bg-white/20 backdrop-blur text-white flex items-center justify-center">
            <Wallet className="h-6 w-6" />
          </div>
          <div className="flex-1">
            <div className="text-xs text-white/80 uppercase tracking-wider mb-1 font-latin font-semibold">المتبقي</div>
            <div className="text-2xl font-extrabold text-white num" data-testid="stat-remaining"><HiddenMoney value={fmtMoney(totalsAll.remaining)} /><span className="iqd-tag !bg-white/20 !text-white">د.ع</span></div>
          </div>
        </div>
      </div>

      {/* Period Collections (Daily / Monthly / Yearly) */}
      <div className="mb-7 bg-white border border-zinc-200 rounded-2xl elevated-card overflow-hidden no-print" data-testid="period-stats-section">
        <div className="px-6 py-4 bg-gradient-to-l from-teal-50 to-white border-b border-zinc-200 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-teal-100 text-teal-700 flex items-center justify-center">
              <BarChart3 className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-xl font-display font-bold text-zinc-900">إحصائيات التسديدات</h2>
              <p className="text-xs text-zinc-500 mt-0.5">اختر الفترة لعرض إجمالي ما تم تحصيله</p>
            </div>
          </div>
          {/* Tabs */}
          <div className="flex bg-zinc-100 rounded-lg p-1">
            {[
              { key: "daily", label: "يومي" },
              { key: "monthly", label: "شهري" },
              { key: "yearly", label: "سنوي" },
            ].map((t) => (
              <button
                key={t.key}
                onClick={() => switchPeriod(t.key)}
                data-testid={`period-tab-${t.key}`}
                className={`px-4 py-2 rounded-md text-sm font-bold transition-all ${
                  periodType === t.key
                    ? "bg-teal-700 text-white shadow-sm"
                    : "text-zinc-600 hover:text-zinc-900"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
        <div className="px-6 py-5">
          <div className="flex items-center gap-3 flex-wrap mb-4">
            <Calendar className="h-5 w-5 text-teal-700" />
            <label className="text-sm font-bold text-zinc-700">
              {periodType === "daily" ? "اختر اليوم:" : periodType === "monthly" ? "اختر الشهر:" : "اختر السنة:"}
            </label>
            {periodType === "daily" && (
              <input
                type="date"
                data-testid="period-date-input"
                value={periodValue}
                onChange={(e) => setPeriodValue(e.target.value)}
                className="px-4 py-2 border-2 border-zinc-200 rounded-lg text-sm font-bold focus:border-teal-600 focus:outline-none num"
              />
            )}
            {periodType === "monthly" && (
              <input
                type="month"
                data-testid="period-month-input"
                value={periodValue}
                onChange={(e) => setPeriodValue(e.target.value)}
                className="px-4 py-2 border-2 border-zinc-200 rounded-lg text-sm font-bold focus:border-teal-600 focus:outline-none num"
              />
            )}
            {periodType === "yearly" && (
              <input
                type="number"
                min="2020"
                max="2100"
                data-testid="period-year-input"
                value={periodValue}
                onChange={(e) => setPeriodValue(e.target.value)}
                className="px-4 py-2 border-2 border-zinc-200 rounded-lg text-sm font-bold focus:border-teal-600 focus:outline-none num w-32"
              />
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gradient-to-br from-teal-700 to-teal-900 rounded-xl p-5 text-white">
              <div className="text-xs uppercase tracking-wider text-teal-100 mb-1 font-latin font-semibold">إجمالي المُحَصَّل</div>
              <div className="text-3xl font-extrabold num" data-testid="period-total">
                <HiddenMoney value={Number(periodCollected.total).toLocaleString("en-US")} />
                <span className="text-xs ms-1 opacity-80">د.ع</span>
              </div>
              <div className="text-xs text-teal-200 mt-2 num">{periodCollected.count} تسديد</div>
            </div>
            <div className="bg-white border border-zinc-200 rounded-xl p-5">
              <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1 font-latin font-semibold">أقساط رئيسية</div>
              <div className="text-2xl font-extrabold text-emerald-600 num">
                <HiddenMoney value={Number(periodCollected.mainTotal).toLocaleString("en-US")} />
              </div>
              <div className="text-xs text-zinc-500 mt-2 num">{periodCollected.mainCount} قسط</div>
            </div>
            <div className="bg-white border border-zinc-200 rounded-xl p-5">
              <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1 font-latin font-semibold">أقساط مشتريات</div>
              <div className="text-2xl font-extrabold text-amber-600 num">
                <HiddenMoney value={Number(periodCollected.purchaseTotal).toLocaleString("en-US")} />
              </div>
              <div className="text-xs text-zinc-500 mt-2 num">{periodCollected.purchaseCount} قسط</div>
            </div>
          </div>
        </div>
      </div>

      {/* Overdue installments alert */}
      {overdueCustomers.length > 0 && (
        <div className="mb-7 bg-white border-2 border-red-200 rounded-2xl elevated-card overflow-hidden no-print" data-testid="overdue-section">
          <div className="px-6 py-4 bg-gradient-to-l from-red-50 to-amber-50 border-b-2 border-red-200 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-xl bg-red-100 text-red-700 flex items-center justify-center animate-pulse">
                <AlertTriangle className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-xl font-display font-bold text-red-700">أقساط متأخرة — تحتاج تذكير</h2>
                <p className="text-xs text-zinc-600 mt-0.5">
                  <span className="num font-bold">{overdueCustomers.length}</span> زبون لم يسدد منذ أكثر من 30 يوماً · إجمالي{" "}
                  <span className="num font-bold text-red-700">{Number(totalOverdueAmount).toLocaleString("en-US")}</span> د.ع
                </p>
              </div>
            </div>
          </div>
          <div className="divide-y divide-zinc-100 max-h-80 overflow-y-auto">
            {overdueCustomers.map((c) => (
              <div
                key={c.id}
                className="px-6 py-3 flex items-center gap-4 hover:bg-red-50/40 transition-colors"
                data-testid={`overdue-row-${c.serial_number}`}
              >
                <div className="inline-flex items-center justify-center h-9 w-9 rounded-lg bg-red-600 text-white font-bold text-sm num shrink-0">
                  {c.days_overdue}
                  <span className="text-[8px] mt-0.5 ms-0.5">يوم</span>
                </div>
                <div className="flex-1 min-w-0">
                  <button
                    onClick={() => navigate(`/customer/${c.id}`)}
                    className="text-base font-bold text-zinc-900 hover:text-teal-700 truncate text-right block w-full"
                  >
                    {c.full_name}
                  </button>
                  <div className="text-xs text-zinc-500 flex items-center gap-2 mt-0.5">
                    <span className="num" style={{ direction: "ltr" }}>{c.phone}</span>
                    <span>·</span>
                    <span>متبقي: <span className="num font-bold text-red-700"><HiddenMoney value={Number(c.remaining).toLocaleString("en-US")} /></span> د.ع</span>
                  </div>
                </div>
                <a
                  href={buildWhatsAppLink(c)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`whatsapp-btn-${c.serial_number}`}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#25D366] hover:bg-[#1FA855] text-white rounded-lg text-sm font-bold shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all shrink-0"
                >
                  <MessageCircle className="h-4 w-4" />
                  إرسال واتساب
                </a>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden elevated-card print-area" data-testid="customers-table">
        <div className="hidden print:block text-center py-4 border-b border-zinc-200">
          <div className="text-2xl font-bold font-display">آغا فون · agha phone</div>
          <div className="text-sm text-zinc-500 mt-1">قائمة الزبائن — {new Date().toLocaleDateString("ar-IQ")} · العدد: {customers.length}</div>
        </div>
        <table className="w-full text-right">
          <thead>
            <tr className="bg-zinc-50 border-b-2 border-zinc-200">
              <th className="px-4 py-4 text-xs font-bold text-zinc-600 uppercase tracking-wider font-latin">#</th>
              <th
                onClick={() => toggleSort("name")}
                data-testid="sort-name-th"
                className="px-4 py-4 text-xs font-bold text-zinc-600 uppercase tracking-wider cursor-pointer hover:text-teal-700 select-none no-print-cursor"
              >
                <span className="inline-flex items-center gap-1.5">
                  الاسم الثلاثي
                  <SortIcon active={sortBy === "name"} dir={sortDir} />
                </span>
              </th>
              <th
                onClick={() => toggleSort("last_payment_date")}
                data-testid="sort-last-payment-th"
                className="px-4 py-4 text-xs font-bold text-zinc-600 uppercase tracking-wider cursor-pointer hover:text-teal-700 select-none"
              >
                <span className="inline-flex items-center gap-1.5">
                  تاريخ آخر تسديد
                  <SortIcon active={sortBy === "last_payment_date"} dir={sortDir} />
                </span>
              </th>
              <th
                onClick={() => toggleSort("total_amount")}
                data-testid="sort-total-th"
                className="px-4 py-4 text-xs font-bold text-zinc-600 uppercase tracking-wider cursor-pointer hover:text-teal-700 select-none"
              >
                <span className="inline-flex items-center gap-1.5">
                  المبلغ الكلي
                  <SortIcon active={sortBy === "total_amount"} dir={sortDir} />
                </span>
              </th>
              <th
                onClick={() => toggleSort("remaining")}
                data-testid="sort-remaining-th"
                className="px-4 py-4 text-xs font-bold text-zinc-600 uppercase tracking-wider cursor-pointer hover:text-teal-700 select-none"
              >
                <span className="inline-flex items-center gap-1.5">
                  المتبقي
                  <SortIcon active={sortBy === "remaining"} dir={sortDir} />
                </span>
              </th>
              <th className="px-4 py-4 text-xs font-bold text-zinc-600 uppercase tracking-wider no-print text-center">حذف</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-16 text-center text-zinc-400">جاري التحميل...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-16 text-center text-zinc-400">
                {customers.length === 0 ? "لا يوجد زبائن — أضف أول زبون من زر +" : "لا توجد نتائج مطابقة"}
              </td></tr>
            ) : (
              filtered.map((c) => {
                const q = query.trim();
                const matches = q && (
                  c.full_name?.toLowerCase().includes(q.toLowerCase()) ||
                  c.phone?.toLowerCase().includes(q.toLowerCase()) ||
                  String(c.serial_number).includes(q)
                );
                return (
                  <tr
                    key={c.id}
                    onClick={() => navigate(`/customer/${c.id}`)}
                    className={`cursor-pointer hover:bg-teal-50/60 transition-colors border-b border-zinc-100 group ${matches ? "row-match" : ""}`}
                    data-testid={`customer-row-${c.serial_number}`}
                  >
                    <td className="px-4 py-4 text-sm font-bold num">
                      <span className="inline-flex items-center justify-center h-7 w-7 bg-zinc-100 text-zinc-700 group-hover:bg-teal-700 group-hover:text-white rounded-md transition-colors">
                        {c.serial_number}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm text-zinc-900 font-semibold">{highlight(c.full_name, query)}</td>
                    <td className="px-4 py-4 text-sm text-zinc-600 num">{c.last_payment_date || "—"}</td>
                    <td className="px-4 py-4 text-sm text-zinc-900 num font-bold"><HiddenMoney value={fmtMoney(c.total_amount)} /></td>
                    <td className="px-4 py-4 text-sm num font-bold">
                      <span className={c.remaining > 0 ? "text-orange-600" : "text-emerald-600"}>
                        <HiddenMoney value={fmtMoney(c.remaining)} />
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm no-print text-center">
                      <button
                        onClick={(e) => { e.stopPropagation(); setDeleting({ id: c.id, name: c.full_name, serial: c.serial_number }); }}
                        data-testid={`delete-customer-btn-${c.serial_number}`}
                        className="text-zinc-400 hover:text-white hover:bg-red-600 h-8 w-8 rounded-lg transition-all inline-flex items-center justify-center"
                        title="حذف الزبون"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* FAB */}
      <button
        onClick={() => setOpenAdd(true)}
        data-testid="add-customer-fab"
        className="fixed bottom-8 left-8 z-50 bg-gradient-to-br from-teal-600 to-teal-800 text-white rounded-2xl h-16 w-16 shadow-xl shadow-teal-700/35 hover:shadow-2xl hover:-translate-y-1 hover:from-teal-700 hover:to-teal-900 transition-all flex items-center justify-center fab-anim no-print"
        title="إضافة زبون جديد"
      >
        <Plus className="h-7 w-7" strokeWidth={3} />
      </button>

      {/* Add Customer Dialog */}
      <Dialog open={openAdd} onOpenChange={setOpenAdd}>
        <DialogContent className="max-w-lg" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-display">إضافة زبون جديد</DialogTitle>
            <DialogDescription>املأ معلومات الزبون. سيُضاف مباشرةً إلى القائمة الرئيسية.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitAdd} className="space-y-4">
            <div>
              <Label htmlFor="full_name">الاسم الثلاثي</Label>
              <Input
                id="full_name"
                data-testid="add-customer-name-input"
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                className="mt-1.5 py-5"
              />
            </div>
            <div>
              <Label htmlFor="phone">رقم الهاتف</Label>
              <Input
                id="phone"
                data-testid="add-customer-phone-input"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="mt-1.5 py-5 num"
                style={{ direction: "ltr", textAlign: "right" }}
              />
            </div>
            <div>
              <Label htmlFor="address">العنوان</Label>
              <Input
                id="address"
                data-testid="add-customer-address-input"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                className="mt-1.5 py-5"
              />
            </div>
            <div>
              <Label htmlFor="notes">ملاحظة</Label>
              <Textarea
                id="notes"
                data-testid="add-customer-notes-input"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="total_amount">المبلغ الكلي (د.ع)</Label>
              <Input
                id="total_amount"
                type="number"
                step="any"
                data-testid="add-customer-total-input"
                value={form.total_amount}
                onChange={(e) => setForm({ ...form, total_amount: e.target.value })}
                className="mt-1.5 py-5 num text-lg font-bold"
              />
            </div>
            <DialogFooter className="gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpenAdd(false)}>
                إلغاء
              </Button>
              <Button
                type="submit"
                disabled={saving}
                data-testid="add-customer-save-btn"
                className="bg-teal-700 hover:bg-teal-800 text-white font-bold"
              >
                {saving ? "جاري الحفظ..." : "حفظ الزبون"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent dir="rtl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-xl text-red-700">تأكيد حذف الزبون</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-700">
              هل تريد فعلاً حذف الزبون{" "}
              <span className="font-bold text-zinc-900">"{deleting?.name}"</span>
              {" "}(رقم <span className="num font-bold">#{deleting?.serial}</span>)؟
              <br />
              <span className="text-red-600 font-semibold">سيتم حذف جميع أقساطه أيضاً ولا يمكن التراجع.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>إلغاء</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              data-testid="confirm-delete-customer-btn"
              className="bg-red-600 hover:bg-red-700 font-bold"
            >
              نعم، احذف
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
